<template>
  <iModal
    :visible="showAddEventModal"
    :show-secondary-action-button="false"
    :max-width="550"
    :title="eventModalTitle"
    :primary-action-button-loading="isLoading"
    :primary-action-button-disabled="isLoading || isUploadingImage"
    :primary-action-button-label="eventModalSubmitButtonLabel"
    @close="closeAddEventModal"
    @click:primary="updateOrCreateEvent"
  >
    <iColumn gap="large">
      <iTextInput
        ref="title"
        v-model="currentEvent.title"
        label="Title"
        placeholder="Title"
        type="text"
        required
        :focus="titleFocus"
        @keydown.enter="updateOrCreateEvent"
      />

      <iTextArea
        v-model="currentEvent.description"
        label="Description"
        placeholder="Description"
        type="text"
        width="fill"
        @keydown.enter="updateOrCreateEvent"
      />

      <iDatePicker
        :model-value="eventDatePickerValue"
        label="Event Date"
        placeholder="Event Date"
        @update:model-value="value => setEventTimestamp('eventTimestamp', value)"
        @keydown.enter="updateOrCreateEvent"
      />

      <iCheckbox
        v-model="currentEvent.isAllDay"
        label="All day event"
      />

      <iRow v-if="!currentEvent.isAllDay" gap="small">
        <iColumn>
          <iTimePicker
            v-model="currentEvent.startTime"
            label="Start Time"
            name="start-time"
            width="fill"
          />
        </iColumn>
        <iColumn>
          <iTimePicker
            v-model="currentEvent.endTime"
            label="End Time"
            name="end-time"
            width="fill"
          />
        </iColumn>
      </iRow>

      <iSelect
        v-model="currentEvent.eventType"
        label="Event Type"
        :items="EVENT_TYPE_OPTIONS"
        value-field="value"
        text-field="label"
        :return-value="true"
        empty-string="Select event type"
        width="fill"
      />

      <iText
        v-if="isLivestream"
        variant="subtle"
        font-size="small"
      >
        Visitors cannot enable camera or microphone unless promoted to special guest or co-host.
      </iText>

      <iCheckbox
        v-model="currentEvent.hideChatOutsideMeeting"
        label="Hide chat before and after meeting"
      />
      <iCheckbox
        v-model="currentEvent.allowEarlyJoin"
        label="People can join before the meeting starts"
      />

      <template v-if="shouldShowEventExtras">
        <iCheckbox
          v-model="currentEvent.allowTicketPurchase"
          label="Allow guests to purchase a ticket"
        />
        <iTextInput
          v-if="currentEvent.allowTicketPurchase"
          v-model="currentEvent.ticketPrice"
          label="Ticket price (USD)"
          type="number"
          min="0"
          step="0.01"
        />
      </template>

      <iRecurrencePicker
        v-model="recurrenceValue"
        :presets="recurrencePresets"
        :default-value="recurrenceDefaultValue"
        :custom-summary="recurrenceSummary"
        :monthly-modes="recurrenceMonthlyModes"
        label="Repeats"
        end-date-placeholder="Last one on"
        width="fill"
      />

      <iRow>
        <iImageContainer
          v-if="currentEvent.imageUrl"
          :src="currentEvent.imageUrl"
          :height="100"
          width="fill"
        />
        <iUploader @upload-file="uploadImage">
          <iButton
            variant="secondary"
            size="small"
            :is-loading="isUploadingImage"
            :disabled="isUploadingImage"
          >
            {{ currentEvent.imageUrl ? "Change" : "Select" }} Image
          </iButton>
        </iUploader>
      </iRow>

      <iText v-if="addEventError" variant="error">
        {{ addEventError }}
      </iText>
    </iColumn>
  </iModal>
  <EventSaveScopeModal
    :visible="showSaveScopeModal"
    :summary="saveScopeSummary"
    :is-loading="isLoading"
    @confirm="confirmSaveScope"
    @cancel="cancelSaveScope"
  />
</template>

<script>
import dayjs from "dayjs";
import { mapState, mapActions, mapWritableState } from "pinia";
import { useEventStore } from "%/stores/event-store";
import {
  EVENT_TYPE_LIVESTREAM,
  EVENT_TYPE_OPTIONS,
  TICKET_CONFIG_EVENT_TYPES,
} from "%/constants/event-constants";
import {
  fromPickerValue,
  monthlyModeOptions,
  pickerDefaultValue,
  pickerPresets,
  pickerSummary,
  toPickerValue,
} from "%/utils/event-recurrence-picker";
import { useCreatorPublicationStore } from "%/stores/creator-publication-store";
import { validateEventRecurrence } from "%/utils/event-helper";
import EventSaveScopeModal from "%/components/events/EventSaveScopeModal.vue";

export default {
  name: "CreateUpdateEventModal",
  components: { EventSaveScopeModal },
  data() {
    return {
      titleFocus: false,
      EVENT_TYPE_OPTIONS,
    };
  },
  computed: {
    ...mapState(useEventStore, [
      "showSaveScopeModal",
      "saveScopeSummary",
      "isLoading",
      "isUploadingImage",
      "showAddEventModal",
      "editEventId",
    ]),
    ...mapWritableState(useEventStore, ["currentEvent", "addEventError"]),
    ...mapState(useCreatorPublicationStore, ["publicationId"]),
    eventModalTitle() {
      return this.editEventId ? "Edit Event" : "Add Event";
    },
    eventModalSubmitButtonLabel() {
      return this.editEventId ? "Update" : "Create";
    },
    eventDatePickerValue() {
      return this.currentEvent.eventTimestamp
        ? this.formatDateInputFromTimestamp(this.currentEvent.eventTimestamp)
        : null;
    },
    recurrenceAnchorTimestamp() {
      return Number(this.currentEvent.eventTimestamp) || 0;
    },
    recurrencePresets() {
      return pickerPresets(this.recurrenceAnchorTimestamp);
    },
    recurrenceDefaultValue() {
      return pickerDefaultValue(this.recurrenceAnchorTimestamp);
    },
    recurrenceMonthlyModes() {
      return monthlyModeOptions(this.recurrenceAnchorTimestamp);
    },
    recurrenceSummary() {
      if (!this.currentEvent.recurrenceRule) {
        return "";
      }

      return pickerSummary(this.currentEvent.recurrenceRule, this.recurrenceAnchorTimestamp);
    },
    recurrenceValue: {
      get() {
        return toPickerValue(this.currentEvent.recurrenceRule, this.recurrenceAnchorTimestamp);
      },
      set(value) {
        this.currentEvent.recurrenceRule = fromPickerValue(value);
      },
    },
    shouldShowEventExtras() {
      return (
        !!this.currentEvent.eventType &&
          TICKET_CONFIG_EVENT_TYPES.includes(this.currentEvent.eventType)
      );
    },
    isLivestream() {
      return this.currentEvent.eventType === EVENT_TYPE_LIVESTREAM;
    },
  },
  watch: {
    showAddEventModal(isVisible) {
      if (isVisible) {
        this.titleFocus = true;
        this.initCurrentEvent();
      }
    },
    "currentEvent.eventType"(eventType) {
      if (!TICKET_CONFIG_EVENT_TYPES.includes(eventType)) {
        this.resetTicketConfiguration();
      }
    },
    "currentEvent.allowTicketPurchase"(isEnabled) {
      if (!isEnabled) {
        this.currentEvent.ticketPrice = 0;
      }
    },
  },
  methods: {
    ...mapActions(useEventStore, [
      "saveEvent",
      "confirmSaveScope",
      "cancelSaveScope",
      "createEvent",
      "uploadImage",
      "updateEvent",
      "closeEventModal",
    ]),
    initCurrentEvent() {
      const event = this.currentEvent;

      if (!event) {
        return;
      }

      if (!this.editEventId) {
        return;
      }

      const hasStartAndEndTimestamps =
          !!event.startTimestamp && !!event.endTimestamp;

      if (event.seriesEventTimestamp) {
        event.eventTimestamp = event.seriesEventTimestamp;
      }

      if (hasStartAndEndTimestamps) {
        const startDate = new Date(event.startTimestamp * 1000);
        const endDate = new Date(event.endTimestamp * 1000);

        event.startTime = startDate.toTimeString().substring(0, 5); // "HH:MM"
        event.endTime = endDate.toTimeString().substring(0, 5);
      } else {
        if (!event.startTime) {
          event.startTime = "09:00";
        }
        if (!event.endTime) {
          event.endTime = "17:00";
        }
      }

      if (!event.recurringStartTimestamp) {
        event.recurringStartTimestamp = 0;
      }
      if (!event.recurringEndTimestamp) {
        event.recurringEndTimestamp = 0;
      }
    },
    closeAddEventModal() {
      this.closeEventModal();
    },
    updateOrCreateEvent() {
      if (!this.isEventFormValid()) {
        return;
      }

      if (this.editEventId) {
        this.saveEvent(this.publicationId);
        return;
      }

      this.createEvent(this.publicationId, this.currentEvent);
    },
    setEventTimestamp(fieldName, value) {
      this.currentEvent[fieldName] = Math.round(value);
    },
    resetTicketConfiguration() {
      this.currentEvent.allowTicketPurchase = false;
      this.currentEvent.ticketPrice = 0;
    },
    isEventFormValid() {
      this.addEventError = null;

      if (!this.currentEvent.title) {
        this.addEventError = "An event title is required";
        return false;
      }

      if (!this.currentEvent.eventTimestamp) {
        this.addEventError = "An event date is required";
        return false;
      }

      if (!this.currentEvent.eventType) {
        this.addEventError = "Please select an event type";
        return false;
      }

      if (!this.validateTicketConfiguration()) {
        return false;
      }

      return this.validateRecurringDates();
    },
    validateTicketConfiguration() {
      if (
        !this.shouldShowEventExtras ||
          !this.currentEvent.allowTicketPurchase
      ) {
        return true;
      }

      const numericTicketPrice = Number(this.currentEvent.ticketPrice);

      if (Number.isNaN(numericTicketPrice)) {
        this.addEventError = "Please enter a ticket price";
        return false;
      }

      if (numericTicketPrice < 0) {
        this.addEventError = "Ticket price cannot be negative";
        return false;
      }

      return true;
    },
    validateRecurringDates() {
      this.addEventError = validateEventRecurrence(this.currentEvent) || null;

      return !this.addEventError;
    },
    formatDateInputFromTimestamp(timestamp) {
      return dayjs.unix(timestamp).format("YYYY-MM-DD");
    },
  },
};
</script>
